import { useState, useEffect, useCallback } from 'react';
import {
  getMyLifeData,
  getMyLifeDataForPerson,
  generateChapters,
  updateChapter,
  getChapterTimeline,
  createMilestone,
  createMemory,
} from '../services/chaptersAPI';

/**
 * Hook for managing My Life Chapters data
 * @param {string|null} personId - Optional person ID for guardian view of a child's data
 */
export function useChaptersData(personId = null) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all My Life data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = personId
        ? await getMyLifeDataForPerson(personId)
        : await getMyLifeData();
      setData(response);
    } catch (err) {
      console.error('❌ useChaptersData: Error fetching chapters data:', err);
      console.error('❌ useChaptersData: Error response:', err.response?.data);
      console.error('❌ useChaptersData: Error status:', err.response?.status);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate initial chapters from birth year
  const handleGenerateChapters = useCallback(async (birthYear, hometown = '') => {
    setLoading(true);
    setError(null);
    try {
      const generatedChapters = await generateChapters(birthYear, hometown);
      await fetchData(); // Refresh data after generation
      return generatedChapters; // Return the generated chapters
    } catch (err) {
      console.error('Error generating chapters:', err);
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  // Update chapter (rename, adjust dates)
  const handleUpdateChapter = useCallback(async (chapterId, updates) => {
    try {
      await updateChapter(chapterId, updates);
      await fetchData(); // Refresh data
      return true;
    } catch (err) {
      console.error('Error updating chapter:', err);
      setError(err);
      return false;
    }
  }, [fetchData]);

  return {
    // State
    data,
    loading,
    error,
    profile: data?.profile || null,
    chapters: data?.chapters || [],
    legacyStats: data?.legacyStats || null,
    stories: data?.stories || [],
    hasCompletedOnboarding: data?.profile?.has_completed_onboarding || false,

    // Actions
    refetch: fetchData,
    generateChapters: handleGenerateChapters,
    updateChapter: handleUpdateChapter,
  };
}

/**
 * Hook for managing a single chapter's timeline
 */
export function useChapterTimeline(chapterId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTimeline = useCallback(async () => {
    if (!chapterId || chapterId === 'undefined') return;

    setLoading(true);
    setError(null);
    try {
      const response = await getChapterTimeline(chapterId);
      setData(response);
    } catch (err) {
      console.error('Error fetching chapter timeline:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [chapterId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  return {
    chapter: data?.chapter || null,
    timeline: data?.timeline || {},
    waitingQuestions: data?.waiting_questions || [],
    loading,
    error,
    refetch: fetchTimeline,
  };
}

/**
 * Hook for the Add Milestone flow
 */
export function useAddMilestone() {
  const [step, setStep] = useState(0); // 0 = closed, 1-4 = steps
  const [form, setForm] = useState({
    quickPickType: null,
    title: '',
    location: '',
    year: null,
    month: null,
    assignedChapterId: null,
    voiceBlob: null,
    voiceDuration: null,
    text: '',
    photoFiles: [],
    photoIds: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startFlow = useCallback(() => {
    setStep(1);
    setForm({
      quickPickType: null,
      title: '',
      location: '',
      year: null,
      month: null,
      assignedChapterId: null,
      voiceBlob: null,
      voiceDuration: null,
      text: '',
      photoFiles: [],
      photoIds: [],
    });
    setError(null);
  }, []);

  const closeFlow = useCallback(() => {
    setStep(0);
  }, []);

  const nextStep = useCallback(() => {
    setStep(prev => Math.min(prev + 1, 4));
  }, []);

  const prevStep = useCallback(() => {
    setStep(prev => Math.max(prev - 1, 1));
  }, []);

  const updateForm = useCallback((updates) => {
    setForm(prev => ({ ...prev, ...updates }));
  }, []);

  const saveMilestone = useCallback(async (chapterId) => {
    setSaving(true);
    setError(null);
    try {
      await createMilestone({
        chapter: chapterId || form.assignedChapterId,
        title: form.title,
        year: form.year,
        month: form.month,
        location: form.location,
        quick_pick_type: form.quickPickType || 'custom',
        text: form.text,
        voice_duration: form.voiceDuration,
        photo_ids: form.photoIds,
      });
      setStep(4); // Confirmation step
      return true;
    } catch (err) {
      console.error('Error saving milestone:', err);
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [form]);

  return {
    step,
    form,
    saving,
    error,
    startFlow,
    closeFlow,
    nextStep,
    prevStep,
    updateForm,
    saveMilestone,
  };
}

/**
 * Hook for the Add Memory flow (lighter than milestone)
 */
export function useAddMemory() {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    year: null,
    voiceBlob: null,
    voiceDuration: null,
    text: '',
    photoFiles: [],
    photoIds: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const openForm = useCallback((initialChapterId = null) => {
    setIsOpen(true);
    setForm({
      title: '',
      year: null,
      chapterId: initialChapterId,
      voiceBlob: null,
      voiceDuration: null,
      text: '',
      photoFiles: [],
      photoIds: [],
    });
    setError(null);
  }, []);

  const closeForm = useCallback(() => {
    setIsOpen(false);
  }, []);

  const updateForm = useCallback((updates) => {
    setForm(prev => ({ ...prev, ...updates }));
  }, []);

  const saveMemory = useCallback(async (chapterId) => {
    setSaving(true);
    setError(null);
    try {
      await createMemory({
        chapter: chapterId || form.chapterId,
        title: form.title,
        year: form.year,
        text: form.text,
        voice_duration: form.voiceDuration,
        photo_ids: form.photoIds,
      });
      setIsOpen(false);
      return true;
    } catch (err) {
      console.error('Error saving memory:', err);
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [form]);

  return {
    isOpen,
    form,
    saving,
    error,
    openForm,
    closeForm,
    updateForm,
    saveMemory,
  };
}

export default useChaptersData;
